import React, { useState, useEffect } from "react";
import "./referal.css";
import { getName } from "../utils/cryptoUtils";
import apiClient from "../utils/apiClient";

const Referral = () => {
  const [employeeName, setEmployeeName] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  const [candidateRole, setCandidateRole] = useState("");
  const [candidateMobile, setCandidateMobile] = useState("");
  const [resume, setResume] = useState(null);
  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ================= HELPERS ================= */
  const capitalizeFullName = (str = "") =>
    str
      .toLowerCase()
      .split(" ")
      .filter(Boolean)
      .map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(" ");

  /* ================= PREFILL EMPLOYEE ================= */
  useEffect(() => {
    const userName = getName();
    if (userName) {
      setEmployeeName(capitalizeFullName(userName));
    }
  }, []);

  /* ================= VALIDATIONS ================= */
  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateMobile = (mobile) =>
    /^[0-9]{10}$/.test(mobile);

  const validateForm = () => {
    const newErrors = {};

    if (!employeeName.trim())
      newErrors.employeeName = "Employee name is required";

    if (!candidateName.trim())
      newErrors.candidateName = "Candidate name is required";

    if (!candidateEmail.trim()) {
      newErrors.candidateEmail = "Email is required";
    } else if (!validateEmail(candidateEmail.trim())) {
      newErrors.candidateEmail = "Invalid email format";
    }

    if (!candidateRole.trim())
      newErrors.candidateRole = "Candidate role is required";

    if (!candidateMobile.trim()) {
      newErrors.candidateMobile = "Mobile number is required";
    } else if (!validateMobile(candidateMobile.trim())) {
      newErrors.candidateMobile =
        "Mobile number must be exactly 10 digits";
    }

    if (!resume)
      newErrors.resume = "Resume is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================= RESUME UPLOAD ================= */
  const handleResumeUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        resume: "Only PDF or Word files are allowed",
      }));
      setResume(null);
      return;
    }

    setErrors((prev) => ({ ...prev, resume: "" }));
    setResume(file);
  };

  /* ================= SUBMIT ================= */
  const handleReferralSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formData = new FormData();
    formData.append(
      "employee_name",
      capitalizeFullName(employeeName.trim())
    );
    formData.append(
      "candidate_name",
      capitalizeFullName(candidateName.trim())
    );
    formData.append("candidate_email", candidateEmail.trim());
    formData.append("candidate_role", candidateRole.trim());
    formData.append("candidate_mobile", candidateMobile.trim());
    formData.append("resume", resume);

    try {
      setLoading(true);

      await apiClient("/send-referral/", {
        method: "POST",
        body: formData,
      });

      setShowModal(true);
      handleCancel();
    } catch (error) {
      console.error("Error sending referral:", error);
      alert(
        error?.message ||
          error?.error ||
          "Failed to submit referral."
      );
    } finally {
      setLoading(false);
    }
  };

  /* ================= RESET ================= */
  const handleCancel = () => {
    setCandidateName("");
    setCandidateEmail("");
    setCandidateRole("");
    setCandidateMobile("");
    setResume(null);
    setErrors({});
  };

  /* ================= UI ================= */
  return (
    <div className="referral-container">
      <div className="referral-box">
        <h2>Internal Employee Referral</h2>

        <form
          className="referral-form"
          onSubmit={handleReferralSubmit}
        >
          <div className="form-row">
            <div>
              <input
                type="text"
                placeholder="Your Name"
                value={employeeName}
                readOnly
              />
              {errors.employeeName && (
                <small className="error-text">
                  {errors.employeeName}
                </small>
              )}
            </div>

            <div>
              <input
                type="text"
                placeholder="Candidate Name"
                value={candidateName}
                onChange={(e) =>
                  setCandidateName(
                    capitalizeFullName(e.target.value)
                  )
                }
              />
              {errors.candidateName && (
                <small className="error-text">
                  {errors.candidateName}
                </small>
              )}
            </div>
          </div>

          <div className="form-row">
            <div>
              <input
                type="email"
                placeholder="Candidate Email"
                value={candidateEmail}
                onChange={(e) =>
                  setCandidateEmail(e.target.value)
                }
              />
              {errors.candidateEmail && (
                <small className="error-text">
                  {errors.candidateEmail}
                </small>
              )}
            </div>

            <div>
              <input
                type="text"
                placeholder="Candidate Role"
                value={candidateRole}
                onChange={(e) =>
                  setCandidateRole(e.target.value)
                }
              />
              {errors.candidateRole && (
                <small className="error-text">
                  {errors.candidateRole}
                </small>
              )}
            </div>
          </div>

          <div className="form-row">
            <div>
              <input
                type="text"
                placeholder="Candidate Mobile Number"
                value={candidateMobile}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value))
                    setCandidateMobile(e.target.value);
                }}
                maxLength="10"
              />
              {errors.candidateMobile && (
                <small className="error-text">
                  {errors.candidateMobile}
                </small>
              )}
            </div>

            <div className="file-upload-container">
              <label
                htmlFor="resume"
                className="custom-file-upload"
              >
                Upload Resume
              </label>
              <span className="file-name">
                {resume ? resume.name : "No file chosen"}
              </span>
              <input
                id="resume"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleResumeUpload}
              />
              {errors.resume && (
                <small className="error-text">
                  {errors.resume}
                </small>
              )}
            </div>
          </div>

          <div className="form-buttons">
            <button
              type="submit"
              className="submit-btn22"
              disabled={loading}
            >
              {loading ? "Submitting..." : "Submit Referral"}
            </button>

            <button
              type="button"
              className="cancel-btn22"
              onClick={handleCancel}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* ================= SUCCESS MODAL ================= */}
      {showModal && (
        <div className="modal16-overlay">
          <div className="modal16-content">
            <h3>Referral Submitted</h3>
            <p>
              Your referral has been successfully submitted to HR.
            </p>
            <button
              onClick={() => setShowModal(false)}
              className="modal16-close-btn"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Referral;
